import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../shared/components/table/inline-edit-table";
import { CustomTextField, CustomSelect } from "../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import { useParams } from "react-router-dom";
import { IRawMaterialDetail } from ".";
import { dtrackItService } from "../../../transaction-certificates/tc-service/dtrackit.service";
import useNotification from "../../../../shared/hooks/useNotification";

interface RawMaterialDetailsProps {
  rawMaterialData: IRawMaterialDetail[];
  setRawMaterialData: React.Dispatch<React.SetStateAction<IRawMaterialDetail[]>>;
  editingRowIndex: number;
  setEditingRowIndex: React.Dispatch<React.SetStateAction<number>>;
}



const RawMaterialDetails: React.FC<RawMaterialDetailsProps> = ({
  rawMaterialData,
  setRawMaterialData,
  editingRowIndex,
  setEditingRowIndex
}) => {
  const { formtype } = useParams<string>();
  const hasSubmitTC: boolean = formtype === 'submited-tc' || formtype === 'view-etc';
  const [data, setData] = useState<IRawMaterialDetail[]>(rawMaterialData);
  const { notify } = useNotification();
  const [rawMaterials, setRawMaterials] = useState([]);
  const validationSchema = Yup.object().shape({
    rawMaterialCode: Yup.string().required("Raw Material Code is required"),
    rawMaterialPercentage: Yup.number()
    .typeError("Percentage must be a number")
    .positive("Percentage must be positive").max(100, "Percentage cannot exceed 100")
    .required("Percentage is required").test(
      'percentage-equals-100',
      'percentage-equals-100',
      function (value: any) {
        if (editingRowIndex > -1) {
          let total = data.reduce((sum, item, index) => {
            if (index === editingRowIndex) return sum;
            return sum + parseFloat(item.rawMaterialPercentage);
          }, 0);
          if ((total + value) > 100) {
            notify("Raw Material Percentage cannot be greater than 100", "error")
            return false
          }
        }
        return true;
  
      }
    ),
  });
  const formik = useFormik<IRawMaterialDetail>({
    initialValues: {
      rawMaterialCode: "",
      rawMaterialPercentage: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    const fetchRawMaterialOptions = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes();
        const options = result.data.map((t: any) => ({
          value: t.code_1,
          label: `${t.description_1} (${t.code_1})`,
        }));
        setRawMaterials(options);
      } catch (err) {
        console.error("Error fetching raw material codes", err);
      }
    };
    fetchRawMaterialOptions();
  }, []);

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: IRawMaterialDetail = {
      rawMaterialCode: "",
      rawMaterialPercentage: "",
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    const currentRowValues = formik.values;
    const isNewRow = editingRowIndex === data.length - 1;

    try {
      await validationSchema.validate(currentRowValues, { abortEarly: false });
      if (isNewRow) {
        setData((prevData) => prevData.map((row, index) => 
          index === editingRowIndex ? currentRowValues : row
        ));
      } else {
        formik.setValues(data[editingRowIndex]);
      }
    } catch (validationErrors) {
      if (isNewRow) {
        setData((prevData) => prevData.slice(0, -1));
      } else {
        formik.setValues(data[editingRowIndex]);
      }
    }

    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null) return;

    const updatedRow = formik.values;
    setData((prevData) =>
      prevData.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      )
    );
    setRawMaterialData(data);
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const columns: EditableColumnDef<IRawMaterialDetail>[] = [
    {
      accessorKey: "rawMaterialCode",
      header: "Raw Material Code",
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          name="rawMaterialCode"
          value={value}
          options={rawMaterials}
          enableSearch={true}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Select Raw Material Code"
          sx={{ mb: 2 }}
          error={Boolean(formik.errors.rawMaterialCode && formik.touched.rawMaterialCode)}
      
        />
      ),
    },
    {
      accessorKey: "rawMaterialPercentage",
      header: "Raw Material Percentage",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="rawMaterialPercentage"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Percentage"
          sx={{ mb: 2 }}
          error={Boolean(formik.errors.rawMaterialPercentage && formik.touched.rawMaterialPercentage)}
          helperText={formik.touched.rawMaterialPercentage && formik.errors.rawMaterialPercentage}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setRawMaterialData(updatedData);
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          {!hasSubmitTC && (
            <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid}>
              Add Raw Material Details
            </StyledButton>
          )}
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!hasSubmitTC}
        />
      </Box>
    </div>
  );
};

export default RawMaterialDetails; 